import {
  HStack,
  PageContainer,
  PageHeader,
  Spread,
  Surface,
  Text,
  VStack,
} from '@/components/Atoms'
import {
  useExportInitiativeForMarketing,
  useInitiative,
  useInitiativePayerRows,
} from '@/hooks/queries/credit/useInitiatives'
import { useLocale } from '@/hooks/useLocale'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { formatCurrency } from '@/lib/utils/numbers'
import {
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeftIcon, DownloadIcon, TriangleAlertIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { InitiativeAuditTrailTab } from './-components/InitiativeAuditTrailTab'
import { getScheduleState, localMidnight } from './-data/initiativeDates'
import { InitiativeFlowFooter } from './-components/InitiativeFlowFooter'
import { InitiativeRowActions } from './-components/InitiativeRowActions'
import { InitiativeRowsTab } from './-components/InitiativeRowsTab'
import { InitiativeUpliftTab } from './-components/InitiativeUpliftTab'
import { InitiativeStatusBadge } from './-components/InitiativeStatusBadge'

const WORKSHEET_TABS = ['rows', 'uplift', 'summary', 'audit'] as const
type WorksheetTab = (typeof WORKSHEET_TABS)[number]

const worksheetSearchSchema = z.object({
  tab: z.enum(WORKSHEET_TABS).optional(),
})

export const Route = createFileRoute('/_auth/_modules/credit/initiatives/$id')({
  component: RouteComponent,
  validateSearch: (search) => worksheetSearchSchema.parse(search),
})

/**
 * The initiative worksheet (FUZE-632): four tabs over one container, with the posture decided by
 * status × viewer — preparer-in-draft edits, the pending chain actor reviews, everyone else
 * reads. The footer carries the flow verbs for whichever posture applies.
 */
function RouteComponent() {
  const { t } = useTranslation('credit')
  const { id } = Route.useParams()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const locale = useLocale()
  const { formatDateLong, formatDateWithTime, formatDistanceToNowLocalized } = useLocalizedDate()

  const initiativeId = Number(id)
  const initiativeQuery = useInitiative(initiativeId)
  const payerRowsQuery = useInitiativePayerRows(initiativeId)
  const exportMutation = useExportInitiativeForMarketing(initiativeId)

  const initiative = initiativeQuery.data
  const payerRows = payerRowsQuery.data
  const tab: WorksheetTab = search.tab ?? 'rows'

  if (!initiative) return null

  const isEditable = initiative.status === 'draft' && initiative.viewer.isPreparer
  const { isStartDue, isEndDue } = getScheduleState(initiative)

  const onTabChange = (value: string) => {
    navigate({
      search: (prev) => ({ ...prev, tab: value === 'rows' ? undefined : (value as WorksheetTab) }),
    })
  }

  return (
    <PageContainer maxWidth="wide">
      {/* Sticky header block — mass CLR's -mx/-mt trick cancels the container padding. */}
      <div className="bg-background-level-2 sticky top-0 z-20 -mx-8 -mt-8 space-y-3 border-b px-8 py-3">
        <Button type="button" variant="ghost" size="sm" className="w-fit" asChild>
          <Link to="/credit/initiatives">
            <ArrowLeftIcon />
            {t('initiatives.pageHeader')}
          </Link>
        </Button>
        <Spread align="end">
          <VStack gap="sm">
            <HStack gap="sm" align="center">
              <PageHeader>{initiative.name}</PageHeader>
              <InitiativeStatusBadge
                status={initiative.status}
                pendingSlot={initiative.pendingSlot}
                startDate={initiative.startDate}
                endDate={initiative.endDate}
              />
              {initiative.slaDueAt ? (
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-default">
                        <Text size="xs" tone="muted" as="span">
                          {t('initiatives.status.slaDueIn', {
                            distance: formatDistanceToNowLocalized(new Date(initiative.slaDueAt)),
                          })}
                        </Text>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t('initiatives.status.slaDueBy', {
                        date: formatDateWithTime(new Date(initiative.slaDueAt)),
                      })}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : null}
            </HStack>
            <Text tone="muted">
              {`${formatDateLong(localMidnight(initiative.startDate))} – ${formatDateLong(localMidnight(initiative.endDate))}`}
              {initiative.versionNumber
                ? ` · ${t('initiatives.versionLabel', { number: initiative.versionNumber })}`
                : ''}
              {` · ${t('initiatives.worksheet.payerCount', { count: initiative.payerCount ?? 0 })}`}
              {` · ${t('initiatives.worksheet.calculatedTotal', { amount: formatCurrency(locale, initiative.upliftTotal ?? 0) })}`}
            </Text>
          </VStack>
          <HStack gap="sm" align="center">
            {/* bg-background (secondary chrome) — the page ground is off-white, a ghost button
                here reads as plain text. */}
            <Button
              type="button"
              variant="secondary"
              disabled={exportMutation.isPending}
              onClick={() => exportMutation.mutate()}
            >
              <DownloadIcon />
              {t('initiatives.worksheet.footer.exportForMarketing')}
            </Button>
            <InitiativeRowActions
              initiative={initiative}
              canEdit={initiative.viewer.isPreparer}
              showImportVersion={isEditable}
            />
          </HStack>
        </Spread>
        <Tabs value={tab} onValueChange={onTabChange}>
          <TabsList>
            {WORKSHEET_TABS.map((value) => (
              <TabsTrigger key={value} value={value}>
                {t(`initiatives.worksheet.tabs.${value}`)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {isStartDue || isEndDue ? (
        <Surface tone="warning" radius="md" className="flex w-full items-center gap-2.5 border p-3">
          <TriangleAlertIcon className="size-4 shrink-0" />
          <Text size="xs" className="text-pretty">
            {t(isStartDue ? 'initiatives.banners.startDue' : 'initiatives.banners.endDue')}
          </Text>
        </Surface>
      ) : null}

      {tab === 'rows' && payerRows ? (
        <InitiativeRowsTab initiativeId={initiativeId} data={payerRows} isEditable={isEditable} />
      ) : null}
      {tab === 'uplift' ? (
        <InitiativeUpliftTab initiativeId={initiativeId} isEditable={isEditable} />
      ) : null}
      {tab === 'summary' ? <EmptyTab text={t('initiatives.worksheet.summaryPlaceholder')} /> : null}
      {tab === 'audit' ? (
        <InitiativeAuditTrailTab initiative={initiative} isEditable={isEditable} />
      ) : null}

      {/* The action bar: send / review / approve / start / stop, by posture. */}
      <div className="bg-background-level-2 sticky bottom-0 z-20 -mx-8 -mb-8 border-t px-8 py-3">
        <InitiativeFlowFooter
          initiative={initiative}
          totals={{
            totalRowCount: payerRows?.totals.totalRowCount ?? initiative.rowCount ?? 0,
            reviewedRowCount:
              payerRows?.totals.reviewedRowCount ?? initiative.reviewedRowCount ?? 0,
          }}
        />
      </div>
    </PageContainer>
  )
}

function EmptyTab(props: { text: string }) {
  return (
    <VStack gap="md" className="w-full items-center py-16 text-center">
      <Text tone="muted" className="max-w-xl text-pretty">
        {props.text}
      </Text>
    </VStack>
  )
}
